import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, DataGrid, Spinner, useToast, type DataGridColumn } from '@kss/ui';
import { Pencil, Plus, RefreshCw, Trash2, Users } from 'lucide-react';

/**
 * Unified Party list — aggregates Customers (/crm/Customer) and Suppliers
 * (/purchase/Supplier) into one master with a type badge. "New Party" opens
 * the rich Party form; a row opens it in edit mode for that source entity.
 */

type Kind = 'customer' | 'supplier';
interface PartyRow extends Record<string, unknown> {
  key: string;
  id?: number | string;
  code?: string;
  name?: string;
  kind: Kind;
  partyType: string;
  phone?: string;
  gstin?: string;
  city?: string;
  balance?: number;
}

const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};
const inr = (n: unknown) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export default function PartyList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [rows, setRows] = useState<PartyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | Kind>('all');

  const editParty = useCallback((r: PartyRow) => {
    navigate(`/panel/parties/new?type=${r.kind}&id=${encodeURIComponent(String(r.id ?? r.code ?? ''))}`);
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [cs, ss] = await Promise.all([
        apiClient.get('/crm/Customer').then((r) => pickList(r.data)).catch(() => []),
        apiClient.get('/purchase/Supplier').then((r) => pickList(r.data)).catch(() => []),
      ]);
      const map = (list: Array<Record<string, unknown>>, kind: Kind): PartyRow[] => list
        .filter((r) => !String(r.code ?? '').startsWith('SAMPLE-'))
        .map((r) => {
          let partyType = kind === 'supplier' ? 'Supplier' : 'Customer';
          try { const env = JSON.parse(String(r.description ?? '{}')) as { partyType?: string }; if (env.partyType) partyType = env.partyType; }
          catch { /* no envelope — fall back to entity kind */ }
          return {
            key: `${kind}:${r.id ?? r.code ?? ''}`,
            id: r.id as number, code: String(r.code ?? ''), name: String(r.name ?? ''), kind, partyType,
            phone: r.phone ? String(r.phone) : '', gstin: r.gstin ? String(r.gstin) : '',
            city: r.city ? String(r.city) : '', balance: Number(r.outstandingBalance ?? 0),
          };
        });
      setRows([...map(cs, 'customer'), ...map(ss, 'supplier')]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load parties');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const deleteParty = useCallback(async (r: PartyRow) => {
    if (!window.confirm(`Delete ${r.name || 'this party'}? This cannot be undone.`)) return;
    const base = r.kind === 'customer' ? '/crm/Customer' : '/purchase/Supplier';
    const key = encodeURIComponent(String(r.id ?? r.code ?? ''));
    try {
      // Prefer delete-by-id; fall back to delete-by-code (some entities only
      // accept the code key — see the DELETE-by-code workaround).
      try { await apiClient.delete(`${base}/${key}`); }
      catch { await apiClient.delete(`${base}/${encodeURIComponent(String(r.code ?? r.id ?? ''))}`); }
      toast.success('Deleted', r.name || '');
      setRows((prev) => prev.filter((x) => x.key !== r.key));
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Try again');
    }
  }, [toast]);

  const visible = useMemo(() => (filter === 'all' ? rows : rows.filter((r) => r.kind === filter)), [rows, filter]);
  const counts = useMemo(() => ({
    customer: rows.filter((r) => r.kind === 'customer').length,
    supplier: rows.filter((r) => r.kind === 'supplier').length,
  }), [rows]);

  const columns = useMemo<DataGridColumn<PartyRow>[]>(() => [
    { accessor: 'name', title: 'Party', sortable: true, filterable: true,
      render: (r) => (
        <button onClick={() => editParty(r)} className="font-medium text-primary hover:underline">
          {r.name || '—'}
        </button>
      ) },
    { accessor: 'partyType', title: 'Type', width: 110, render: (r) => <TypePill kind={r.kind} label={r.partyType} /> },
    { accessor: 'phone', title: 'Phone', sortable: true },
    { accessor: 'gstin', title: 'GSTIN', sortable: true },
    { accessor: 'city', title: 'City', sortable: true },
    { accessor: 'balance', title: 'Balance', sortable: true, render: (r) => `₹ ${inr(r.balance)}` },
    { accessor: '__actions' as never, title: '', width: 90,
      render: (r) => (
        <div className="flex items-center gap-1">
          <button onClick={() => editParty(r)} title="Edit" aria-label="Edit"
            className="grid h-7 w-7 place-items-center rounded-md border border-border text-content-muted hover:border-primary hover:text-primary">
            <Pencil size={13} />
          </button>
          <button onClick={() => void deleteParty(r)} title="Delete" aria-label="Delete"
            className="grid h-7 w-7 place-items-center rounded-md border border-border text-content-muted hover:border-danger hover:text-danger">
            <Trash2 size={13} />
          </button>
        </div>
      ) },
  ], [editParty, deleteParty]);

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary"><Users className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Parties</h1>
          <p className="text-xs text-content-muted">{counts.customer} customer(s) · {counts.supplier} supplier(s)</p>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['all', 'customer', 'supplier'] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 text-xs capitalize ${filter === f ? 'bg-primary text-white' : 'bg-surface-card text-content-secondary'}`}>
              {f === 'all' ? 'All' : `${f}s`}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button size="sm" onClick={() => navigate('/panel/parties/new')}>
          <Plus className="h-3.5 w-3.5" /> New Party
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {loading && rows.length === 0 ? (
        <Card><div className="flex items-center gap-2 p-6 text-sm text-content-muted"><Spinner /> Loading parties…</div></Card>
      ) : visible.length === 0 ? (
        <Card><div className="p-6 text-sm text-content-muted">No parties yet. Click <em>New Party</em> to add a customer or supplier.</div></Card>
      ) : (
        <DataGrid<PartyRow> data={visible} columns={columns} rowId="key" />
      )}
    </div>
  );
}

function TypePill({ kind, label }: { kind: Kind; label: string }) {
  const cls = kind === 'customer' ? 'border-info/40 bg-info/10 text-info' : 'border-warning/40 bg-warning/10 text-warning';
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{label || (kind === 'customer' ? 'Customer' : 'Supplier')}</span>;
}
